<template>
  <section class="section-base form-section-base form-base">
    <div class="form-section-base__title">
      Прошу удостоверить факт наличия в Едином государственном реестре
      недвижимости сведений о фамилии, об имени, отчестве и о дате рождения
      гражданина, являющегося правообладателем объекта недвижимости или лицом, в
      пользу которого зарегистрированы ограничения права или обременения объекта
      недвижимости, содержащихся в выписке из Единого государственного реестра
      недвижимости
    </div>

    <v-container>
      <div class="form-section-base__controls">
        <v-radio-group
          v-model="egrnConfirmDetails.requestType"
          column
          :error-messages="
            getValidationErrorText($v.egrnConfirmDetails.requestType)
          "
        >
          <v-radio
            label="Об объекте недвижимости"
            value="estateObjectInfoRequest"
          ></v-radio>
          <v-radio
            label="Об основных характеристиках и зарегистрированных правах на объект недвижимости"
            value="mainCharacteristicsRights"
          ></v-radio>
          <v-radio
            label="О зарегистрированных договорах участия в долевом строительстве"
            value="contractsSharedEquityConstruction"
          ></v-radio>
          <v-radio
            label="О переходе прав"
            value="isTransferOfRights"
          ></v-radio>
        </v-radio-group>
      </div>

      <v-row>
        <v-col cols="12">
          <TextField
            v-model="$v.egrnConfirmDetails.estateObjectKN.$model"
            mask="9{2}:9{2}:9{6,7}:9{+}"
            :errorMessages="
              getValidationErrorText($v.egrnConfirmDetails.estateObjectKN)
            "
            label="Объект недвижимости (кадастровый номер)"
          />
        </v-col>
        <v-col cols="12">
          <Autocomplete
            v-model="$v.egrnConfirmDetails.rfRegion.$model"
            label="Субъект Российской Федерации в котором находится объект недвижимости"
            item-text="regionName"
            item-value="regionCode"
            :errorMessages="
              getValidationErrorText($v.egrnConfirmDetails.rfRegion)
            "
            :items="regions"
            :filter="
              (item, queryText) => {
                return (
                  item.regionName
                    .toLocaleLowerCase()
                    .indexOf(queryText.toLocaleLowerCase()) > -1
                );
              }
            "
          >
            <template v-slot:item="data">
              <v-list-item-content>
                <v-list-item-title>{{
                  data.item.regionName
                }}</v-list-item-title>
                <v-list-item-subtitle>
                  код региона: {{ data.item.regionCode }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </Autocomplete>
        </v-col>
      </v-row>

      <Footer
        @next-btn-click="btnSaveClickHandler(StepDirection.NEXT)"
        @prev-btn-click="btnSaveClickHandler(StepDirection.PREV)"
        :loading-next="loadingNext"
      />
    </v-container>
  </section>
</template>

<script>
import cloneDeep from 'clone-deep';
import { checkInvalid, leaveStep } from '@/utils/services';
import { Role } from '@/utils/dictionaries';
import { ServicesModule } from '@/store/modules/services';
import { StepDirection, StepName } from '@/types/services';
import { minLength, required } from 'vuelidate/lib/validators';
import { getValidationErrorText } from '@/utils/validate';
import Footer from '@/components/services/elements/Footer';
import TextField from '@/components/base/TextField';
import { RegionModule } from '@/store/modules/region';
import deepmerge from 'deepmerge';
import Autocomplete from '@/components/base/Autocomplete';
import { EgrnConfirmStep } from '@/constants';

const RequstType = {
  EstateObjectInfoRequest: 'estateObjectInfoRequest',
  MainCharacteristicsRights: 'mainCharacteristicsRights',
  ContractsSharedEquityConstruction: 'contractsSharedEquityConstruction',
  isTransferOfRights: 'isTransferOfRights'
};

const getDefaultForm = () => ({
  requestType: null,
  estateObjectKN: '',
  rfRegion: '',
});

export default {
  name: 'EstateObject',
  props: {
    loadingNext: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    Footer,
    TextField,
    Autocomplete,
  },
  data: () => ({
    getValidationErrorText,
    StepDirection,
    StepName,
    leaveStep,
    Role,
    invalidFields: [],
    egrnConfirmDetails: getDefaultForm(),
  }),
  computed: {
    regions() {
      return RegionModule.egrnConfirmRegions;
    },
  },
  validations() {
    return {
      egrnConfirmDetails: {
        requestType: {
          required,
        },
        estateObjectKN: {
          required,
          minLength: minLength(14),
        },
        rfRegion: {
          required,
        },
      },
    };
  },
  created() {
    this.prefillDraft();
  },
  methods: {
    prefillDraft() {
      const detailsStore = cloneDeep(
        ServicesModule.statement.egrnConfirmDetails
      );

      if (!detailsStore?.estateObjectKN) return;

      this.egrnConfirmDetails = deepmerge.all([
        getDefaultForm(),
        detailsStore,
        {
          requestType:
            (detailsStore.estateObjectInfoRequest &&
              RequstType.EstateObjectInfoRequest) ||
            (detailsStore.mainCharacteristicsRights &&
              RequstType.MainCharacteristicsRights) ||
            (detailsStore.contractsSharedEquityConstruction &&
              RequstType.ContractsSharedEquityConstruction) ||
            (detailsStore.isTransferOfRights &&
                RequstType.isTransferOfRights) ||
            null,
        },
      ]);
    },

    touchFields() {
      this.invalidFields = [];
      this.$v.$touch();
      checkInvalid(this.$v, this.invalidFields);
    },

    assemblyModel() {
      return deepmerge(this.egrnConfirmDetails, {
        estateObjectInfoRequest:
          this.egrnConfirmDetails.requestType ===
          RequstType.EstateObjectInfoRequest,
        mainCharacteristicsRights:
          this.egrnConfirmDetails.requestType ===
          RequstType.MainCharacteristicsRights,
        contractsSharedEquityConstruction:
          this.egrnConfirmDetails.requestType ===
          RequstType.ContractsSharedEquityConstruction,
        isTransferOfRights:
            this.egrnConfirmDetails.requestType ===
            RequstType.isTransferOfRights,
      });
    },

    btnSaveClickHandler(direction) {
      leaveStep({
        stepThis: this,
        direction,
        step: EgrnConfirmStep.ESTATE_OBJECT,
      });
    },
  },
};
</script>
